import { CatalogOverplotModule } from "@/modules/CatalogOverplotModule"
import { CatalogOverplot } from "@/modules/CatalogOverplotModule/CatalogOverplot"
import { Frame, store } from "@/store"
import { download } from "@/utils/file"
import { Component, Prop, Vue, Watch } from "vue-property-decorator"
import { EquatorialCoord } from "@hscmap/angle"
import { MarkerLayer } from "@/modules/CatalogOverplotModule/Marker"
import { MarkerStyle } from "@/modules/CatalogOverplotModule/Marker/marker"
import scrollIntoView from 'scroll-into-view-if-needed'

const idRegexp = /^[a-z_]\w*$/i

@Component
export default class extends Vue {
  @Prop({ required: true })
  module!: CatalogOverplotModule

  @Prop({ required: true })
  co!: CatalogOverplot

  @Prop({ required: true })
  frame!: Frame

  private varDecs() {
    const decs = ['var $ = {}']
    this.co.header.forEach((colname, i) => {
      if (idRegexp.test(colname)) {
        decs.push(`var ${colname} = $row[${i}]`)
      } else {
        decs.push(`$[${JSON.stringify(colname)}] = $row[${i}]`)
      }
    })
    return decs.join(';')
  }

  private rowEvaluator<T>(js: string): (i: number) => T {
    const jsf = `(function($rows){
      return function($i) {
        var $row = $rows[$i];
        ${this.varDecs()};
        return (${js});
      }
    })`
    try {
      // tslint:disable-next-line:no-eval
      return eval(jsf)(this.co.rows)
    } catch (e) {
      console.warn(jsf)
      throw e
    }
  }

  sortReverse = false
  sortJs = ''
  sortJsError = null as null | string
  sortJsFor(colname: string) {
    return idRegexp.test(colname) ? colname : `$[${JSON.stringify(colname)}]`
  }
  get sortFunc() {
    const js = this.sortJs
    this.sortJsError = null
    if (js.trim().length > 0) {
      try {
        return this.rowEvaluator<number>(js)
      } catch (e) {
        this.sortJsError = e
      }
    }
    return null
  }

  filterJs = ''
  filterJsError = null as null | string
  get filterFunc() {
    const js = this.filterJs
    this.filterJsError = null
    if (js.trim().length > 0) {
      try {
        return this.rowEvaluator<boolean>(js)
      } catch (e) {
        this.filterJsError = e
      }
    }
    return null
  }
  @Watch('filterFunc')
  onFilterFuncChagne() {
    this.co.tableFilterFunc = this.filterFunc
  }

  get sortedIndices() {
    if (this.sortFunc) {
      const indices = this.co.filteredIndices.slice().sort((a, b) =>
        (this.sortReverse ? -1 : 1) * compare(this.sortFunc!(a), this.sortFunc!(b)),
      )
      return indices
    }
    return this.co.filteredIndices
  }

  get i2j() {
    const m = new Map<number, number>()
    for (let j = 0; j < this.sortedIndices.length; ++j) {
      const i = this.sortedIndices[j]
      m.set(i, j)
    }
    return m
  }

  page = 1
  per = 250
  get pagedIndices() {
    return this.sortedIndices.slice((this.page - 1) * this.per, this.page * this.per)
  }
  get numPages() {
    return Math.floor((this.sortedIndices.length - 1) / this.per) + 1
  }
  @Watch('numPages')
  onNumPagesChange() {
    this.page = 1
  }

  showColumn = this.co.header.map((h) => true)

  get visibleColumns() {
    const indices: number[] = []
    this.showColumn.forEach((s, i) => s && indices.push(i))
    return indices
  }

  async downloadCsv(onlyChecked: boolean) {
    const { stringify } = await import('@/external/csv-stringify')
    const co = this.co
    const rows = (() => {
      let indices = this.sortedIndices
      if (onlyChecked) {
        indices = indices.filter((i) => this.co.selected.indexOf(i) >= 0)
      }
      return indices.map((i) => this.co.rows[i])
    })()
    const header = co.header.slice()
    const csvRows = [header].concat(rows)
    header[0] = `# ${header[0]}`
    const csv = stringify(csvRows)
    download('hscMap-catalog.csv', `data:text/csv,${encodeURIComponent(csv)}`)
  }

  focusFollowsMouseCursor = false
  focusFollowsArrowKeys = true

  goto(i: number) {
    const coord = EquatorialCoord.fromXyz(this.co.coord3[i])
    store.globe(this.frame).viewFactory.jumpTo({}, { coord, duration: 100, keepFovy: true })
  }

  private focusLayer?: MarkerLayer

  private focusMarker(i: number) {
    this.clearFocusMarker()
    const globe = store.globe(this.frame)
    const xyz = this.co.coord3[i]
    this.focusLayer = new MarkerLayer([xyz], [1, 1, 1, 1], MarkerStyle.CROSS3)
    globe.addLayer(this.focusLayer)
  }

  private clearFocusMarker() {
    const globe = store.globe(this.frame)
    this.focusLayer && globe.removeLayer(this.focusLayer)
    this.focusLayer = undefined
  }

  focusedIndex: number | null = null
  hasFocus = false

  @Watch('focusedIndex')
  onFocusedIndexChange(i: number | null) {
    if (i !== null) {
      this.focusMarker(i)
      this.focusFollowsMouseCursor && this.goto(i)
      this.$nextTick(() => {
        const j = this.pagedIndices.indexOf(i)
        if (j >= 0) {
          const tr = (this.$refs.tbody as HTMLElement).children.item(j)
          tr && scrollIntoView(tr, { scrollMode: 'if-needed', block: 'nearest' })
        }
      })
    } else {
      this.clearFocusMarker()
    }
  }

  onKeydown(e: KeyboardEvent) {
    if (e.target !== e.currentTarget) {
      return
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const d = e.key === 'ArrowDown' ? +1 : this.sortedIndices.length - 1
      const j = (this.i2j.get(this.focusedIndex || 0)! + d) % this.sortedIndices.length
      this.focusedIndex = this.sortedIndices[j]
      this.page = Math.floor(j / this.per) + 1
      this.focusFollowsMouseCursor || this.focusFollowsArrowKeys && this.goto(this.focusedIndex)
    }
    if (e.key === ' ') {
      e.preventDefault()
      const i = this.focusedIndex
      if (i != null) {
        const k = this.co.selected.indexOf(i)
        if (k < 0) {
          this.co.selected.push(i)
        } else {
          this.co.selected.splice(k, 1)
        }
      }
    }
  }

  mouseenter(i: number) {
    this.hasFocus || (this.focusedIndex = i)
  }

  mouseleave(i: number) {
    this.hasFocus || (this.focusedIndex = null)
  }

  deleteRow(rowIndex: number) {
    if (confirm('Delete ?')) {
      setTimeout(()=>{
        this.co.coord3.splice(rowIndex, 1)
        this.co.rows.splice(rowIndex, 1)
        this.focusedIndex = null
        this.clearFocusMarker()
      })
    }
  }
}

function compare(a: any, b: any): number {
  if (typeof a === 'number' && typeof b === 'number') {
    if (!isFinite(a) && !isFinite(b)) {
      return 0
    }
    if (!isFinite(a)) {
      return 1
    }
    if (!isFinite(b)) {
      return -1
    }
    return a - b
  } else {
    return a === b ? 0 : (a < b ? -1 : +1)
  }
}
